import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Upload, Input, Image, Checkbox, Space, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';

const { Dragger } = Upload;

// Validation helpers
const defaultMaxSizeBytes = 2 * 1024 * 1024; // 2MB
const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

function validateFile(file, maxSizeBytes = defaultMaxSizeBytes) {
  if (!file) return { valid: true };
  if (file.size > maxSizeBytes) {
    return { valid: false, error: 'Image must be smaller than 2MB' };
  }
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'Only JPG, PNG, GIF, and WebP images are allowed' };
  }
  return { valid: true };
}

const urlUtils = {
  isValidUrl(url) {
    if (!url) return false;
    try { new URL(url); return true; } catch { return false; }
  },
  isYouTubeUrl(url) {
    if (!url) return false;
    return /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\?\/]+)/.test(url);
  },
  extractYouTubeId(url) {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\?\/]+)/);
    return match ? match[1] : null;
  },
  getYouTubeThumbnail(videoUrl) {
    const id = this.extractYouTubeId(videoUrl);
    return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : null;
  }
};

/**
 * ThumbnailInput
 * value can be: File object, URL string, or null
 * onChange receives: File | string | null
 */
const ThumbnailInput = ({
  value,
  onChange,
  label,
  maxSize = 2,
  showUrlOption = true,
  autoExtractYouTube = true,
  videoUrl,
}) => {
  const initialMode = showUrlOption ? (value instanceof File ? 'upload' : 'url') : 'upload';
  const [mode, setMode] = useState(initialMode);
  const [fileList, setFileList] = useState([]);
  const [url, setUrl] = useState(typeof value === 'string' ? value : '');
  const [autoExtract, setAutoExtract] = useState(autoExtractYouTube);

  useEffect(() => {
    if (value instanceof File) {
      setMode('upload');
      setFileList([{ uid: '-1', name: value.name, status: 'done', originFileObj: value }]);
    } else if (typeof value === 'string') {
      setMode(showUrlOption ? 'url' : 'upload');
      setUrl(value);
      setFileList([]);
    } else {
      setFileList([]);
      setUrl('');
    }
  }, [value]);

  const maxSizeBytes = maxSize * 1024 * 1024;

  const youTubeThumb = useMemo(() => {
    if (!autoExtract || !urlUtils.isYouTubeUrl(videoUrl || '')) return null;
    return urlUtils.getYouTubeThumbnail(videoUrl);
  }, [autoExtract, videoUrl]);

  const currentPreview = useMemo(() => {
    if (mode === 'upload' && fileList[0] && fileList[0].originFileObj) {
      return URL.createObjectURL(fileList[0].originFileObj);
    }
    // When URL mode is enabled, preview valid url. If URL mode is hidden, still preview provided url (existing image)
    if ((mode === 'url' && urlUtils.isValidUrl(url)) || (!showUrlOption && urlUtils.isValidUrl(url))) return url;
    if (youTubeThumb) return youTubeThumb;
    return null;
  }, [mode, fileList, url, youTubeThumb]);

  const beforeUpload = (file) => {
    const { valid, error } = validateFile(file, maxSizeBytes);
    if (!valid) {
      message.error(error);
      return Upload.LIST_IGNORE;
    }
    // store locally, do not auto-upload
    setFileList([{ uid: file.uid || String(Date.now()), name: file.name, status: 'done', originFileObj: file }]);
    onChange && onChange(file);
    return false;
  };

  const onRemove = () => {
    setFileList([]);
    onChange && onChange(null);
  };

  const onUrlChange = (e) => {
    const v = e.target.value;
    setUrl(v);
    onChange && onChange(v || null);
  };

  const items = [
    {
      key: 'upload',
      label: 'Upload Image',
      children: (
        <Dragger
          name="file"
          multiple={false}
          beforeUpload={beforeUpload}
          fileList={fileList}
          onRemove={onRemove}
          accept={allowedTypes.map(t => `.${t.split('/')[1]}`).join(',')}
          openFileDialogOnClick
          maxCount={1}
          showUploadList={{ showRemoveIcon: true }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Click or drag image to this area to upload</p>
          <p className="ant-upload-hint">Max {maxSize}MB. Allowed: JPG, PNG, GIF, WebP.</p>
        </Dragger>
      )
    }
  ];

  if (showUrlOption) {
    items.push({
      key: 'url',
      label: 'Use URL',
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input placeholder="https://example.com/image.jpg" value={url} onChange={onUrlChange} />
          {autoExtractYouTube && (
            <Checkbox checked={autoExtract} onChange={e => setAutoExtract(e.target.checked)}>
              Auto-extract from YouTube when possible
            </Checkbox>
          )}
        </Space>
      )
    });
  }

  return (
    <div>
      {label && <div style={{ marginBottom: 8 }}>{label}</div>}
      <Tabs items={items} activeKey={mode} onChange={setMode} />
      {currentPreview && (
        <div style={{ marginTop: 12 }}>
          <Image src={currentPreview} alt="preview" width={200} style={{ objectFit: 'cover' }} />
        </div>
      )}
      {(currentPreview || fileList.length > 0 || url) && (
        <div style={{ marginTop: 8 }}>
          <a onClick={() => { setFileList([]); setUrl(''); onChange && onChange(null); }}>Clear</a>
        </div>
      )}
    </div>
  );
};

export default ThumbnailInput;
